import type { CollaboratorDto, CollaboratorHistorySliceDto } from '@/api/ApiClient';

export type TimesliceEditTarget = { kind: 'current-open' } | { kind: 'historical'; validFrom: string; validTo: string };

export function timesliceKey(validFrom?: string, validTo?: string): string {
  return `${validFrom ?? ''}|${validTo ?? ''}`;
}

/** True when ValidTo is the open-ended sentinel (current slice). */
export function isOpenTimeslice(validTo?: string): boolean {
  if (!validTo) {
    return true;
  }
  const normalized = validTo.trim();
  return normalized.includes('9999') || normalized.startsWith('9999');
}

export function isCurrentOpenTarget(
  target: TimesliceEditTarget,
  record: Pick<CollaboratorDto, 'validFrom' | 'validTo'> | null | undefined
): boolean {
  if (target.kind === 'current-open') {
    return true;
  }
  if (!record) {
    return false;
  }
  return timesliceKey(target.validFrom, target.validTo) === timesliceKey(record.validFrom, record.validTo);
}

/** Converts API date (M/d/yyyy or ISO) to YYYY-MM-DD for date inputs. */
export function apiDateToInputDate(apiDate: string | undefined): string {
  if (!apiDate?.trim()) {
    return '';
  }
  const trimmed = apiDate.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  const parts = trimmed.split(/[/-]/);
  if (parts.length === 3) {
    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);
    if (month >= 1 && day >= 1 && year > 1900) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return '';
}

/** Today's date as YYYY-MM-DD for HTML date inputs (local calendar day). */
export function todayInputDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Converts YYYY-MM-DD from date input to M/d/yyyy for the admin update API. */
export function inputDateToApiDate(input: string | undefined): string | undefined {
  const trimmed = input?.trim();
  if (!trimmed) {
    return undefined;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) {
    return trimmed;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return `${month}/${day}/${year}`;
}

export function formatHistorySliceSummary(slice: CollaboratorHistorySliceDto, facultyLabel?: string): string {
  const parts: string[] = [];
  if (slice.institution?.trim()) {
    parts.push(slice.institution.trim());
  }
  if (slice.position?.trim()) {
    parts.push(slice.position.trim());
  }
  if (slice.email?.trim()) {
    parts.push(slice.email.trim());
  }
  const faculty = facultyLabel?.trim() || slice.faculty?.trim();
  if (faculty) {
    parts.push(`Faculty: ${faculty}`);
  }
  if (slice.city || slice.state) {
    parts.push([slice.city, slice.state].filter(Boolean).join(', '));
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export type SliceProfileFormFields = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  institution: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  faculty: string | undefined;
};

/** Profile/affiliation fields from a history row (account-level fields unchanged). */
export function profileFieldsFromHistorySlice(slice: CollaboratorHistorySliceDto): SliceProfileFormFields {
  return {
    firstName: String(slice.firstName ?? ''),
    middleName: String(slice.middleName ?? ''),
    lastName: String(slice.lastName ?? ''),
    email: String(slice.email ?? ''),
    phone: String(slice.phone ?? ''),
    position: String(slice.position ?? ''),
    institution: String(slice.institution ?? ''),
    city: String(slice.city ?? ''),
    state: String(slice.state ?? ''),
    zip: slice.zip === null || slice.zip === undefined ? '' : String(slice.zip),
    country: String(slice.country ?? 'USA'),
    faculty: slice.faculty?.trim() ? slice.faculty.trim() : undefined,
  };
}
